/**
 * @fileoverview LocationInputRenderer component for the Storyteller web application.
 * Renders a location input form element with autocomplete suggestions.
 *
 * @example
 * // Basic usage
 * <LocationInputRenderer path="location-input" handleChange={handleChange} />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete'
import { useScript } from '../../../../hooks/useScript'
import css from './InputRenderer.module.css'

/**
 * @interface LocationData
 * @description Interface for location data.
 * @property {string} address - The address of the location.
 * @property {number} lat - The latitude of the location.
 * @property {number} lng - The longitude of the location.
 */
interface LocationData {
    address: string
    lat: number
    lng: number
}

/**
 * @interface LocationInputProps
 * @description Interface for the LocationInputRenderer component.
 * @property {string} data - The data for the location input.
 * @property {string} label - The label for the location input.
 * @property {boolean} required - Whether the location input is required.
 * @property {function} handleChange - The function to handle the change of the location input.
 * @property {string} path - The path of the location input.
 */
interface LocationInputProps extends ControlProps {
    data: string
    label: string
    required?: boolean
    handleChange(path: string, value: LocationData): void
    path: string
}

/**
 * @function classNames
 * @description Helper function to join classes with spaces.
 * @param {...string[]} classes - The classes to join.
 * @returns {string} The joined classes.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function LocationInputRenderer
 * @description Component for rendering a location input form element with autocomplete suggestions.
 * @param {LocationInputProps} props - The props for the LocationInputRenderer component.
 * @returns {JSX.Element} The LocationInputRenderer component.
 */
function LocationInputRenderer({ uischema, path, handleChange, errors }: LocationInputProps) {
    const [address, setAddress] = useState('')
    const { t } = useTranslation()
    const hasError = errors.length > 0
    const isReady = useScript([
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyCNLq7aVizUH5r1e_HzWkTzQwcmygrqUSs&libraries=places',
    ])

    if (!isReady) {
        return null
    }

    const handleSelect = async (selectedAddress: string) => {
        setAddress(selectedAddress)
        if (!selectedAddress) {
            handleChange(path, { address: selectedAddress, lat: undefined, lng: undefined })
            return
        }

        try {
            const results = await geocodeByAddress(selectedAddress)
            const { lat, lng } = await getLatLng(results[0])
            handleChange(path, { address: selectedAddress, lat, lng })
        } catch (error) {
            console.error('Error fetching geocode data:', error)
        }
    }

    return (
        <>
            <PlacesAutocomplete
                value={address}
                searchOptions={{
                    types: ['locality', 'country', 'postal_code'],
                    componentRestrictions: { country: uischema.options.country },
                }}
                onChange={handleSelect}
                onSelect={handleSelect}
            >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div className={css.inputContainer}>
                        <input
                            {...getInputProps({
                                className: classNames(css.input, hasError && css.error),
                                id: path,
                                name: path,
                                placeholder: t(uischema.options.placeholder),
                            })}
                        />
                        {suggestions.length ? (
                            <div className={css.suggestionContainer}>
                                {loading && <div className="p-2">Loading...</div>}
                                {suggestions.slice(0, 3).map((suggestion) => {
                                    const className = classNames(
                                        css.suggestion,
                                        suggestion.active ? css.activeSuggestion : null
                                    )

                                    return (
                                        <div
                                            key={suggestion.placeId}
                                            {...getSuggestionItemProps(suggestion, { className })}
                                        >
                                            {suggestion.description}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : null}
                    </div>
                )}
            </PlacesAutocomplete>
            {hasError && <div className={css.errorMessage}>{errors}</div>}
        </>
    )
}

export const LocationInputTester = rankWith(3, uiTypeIs('Location'))

export default withJsonFormsControlProps(LocationInputRenderer)
